import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import HomeSection from "../../components/store/HomeSection";
import CampaignsRail from "../../components/store/CampaignsRail";
import SocialRail from "../../components/store/SocialRail";
import BookNowBanner from "../../components/store/BookNowBanner";
import { useBranding } from "../../context/BrandingContext";
import {
  KalkiHero, KalkiCategoryGrid,
  MyntraHero, MyntraCategoryGrid,
  AjioHero, AjioCategoryGrid,
  MangoHero, MangoCategoryGrid,
  BibaHero, BibaCategoryGrid,
  NeerusHero, NeerusCategoryGrid,
  TrendingGrid,
} from "./home/heroVariants";

const HERO_BY_PRESET = {
  kalki: KalkiHero,
  myntra: MyntraHero,
  ajio: AjioHero,
  mango: MangoHero,
  biba: BibaHero,
  neerus: NeerusHero,
};
const CATEGORY_BY_PRESET = {
  kalki: KalkiCategoryGrid,
  myntra: MyntraCategoryGrid,
  ajio: AjioCategoryGrid,
  mango: MangoCategoryGrid,
  biba: BibaCategoryGrid,
  neerus: NeerusCategoryGrid,
};

const FEATURED_IMG = "https://images.unsplash.com/photo-1740431377901-c2f28d50c759?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const BRAND_IMG = "https://images.pexels.com/photos/36489477/pexels-photo-36489477.jpeg";

export default function Home() {
  const { branding } = useBranding();
  const preset = branding?.theme_preset || "kalki";
  const Hero = HERO_BY_PRESET[preset] || KalkiHero;
  const CategoryGrid = CATEGORY_BY_PRESET[preset] || KalkiCategoryGrid;

  const [categories, setCategories] = useState([]);
  const [trending, setTrending] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, t, s] = await Promise.all([
          api.get("/categories"),
          api.get("/products?limit=8&sort=new"),
          api.get("/homepage-sections").catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setCategories(c.data);
        setTrending(t.data.items);
        setSections(s.data || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Could not load homepage content.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="home-page" data-theme-preset={preset}>
      <Hero branding={branding} />
      <SocialRail />
      <CategoryGrid categories={categories} />
      <CampaignsRail />
      {sections.map((s) => <HomeSection key={s.id} section={s} />)}

      {/* FEATURED COLLECTION — kept across all presets */}
      <section className="px-6 md:px-12 lg:px-24 py-20 bg-[hsl(var(--kb-surface-alt))]">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="overline mb-3">Editor's Pick</div>
            <h2 className="font-display text-4xl lg:text-6xl leading-tight mb-6">
              The <em className="italic">Maroon</em> Heirloom Edit
            </h2>
            <p className="text-[hsl(var(--kb-muted))] leading-relaxed mb-8">
              Deep crimson silks, zardozi embroideries, and heirloom cuts — pieces that outlive trend cycles and are destined to become tomorrow's vintage.
            </p>
            <Link to="/shop?color=Maroon" className="kb-btn-primary inline-block" data-testid="featured-shop-btn">
              Shop the Edit
            </Link>
          </div>
          <div className="md:col-span-7 aspect-[4/5] md:aspect-[16/11] overflow-hidden">
            <img src={FEATURED_IMG} alt="" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <TrendingGrid products={trending} preset={preset} loading={loading} error={error} />

      {/* BRAND STORY */}
      <section className="grid md:grid-cols-2 min-h-[70vh]">
        <div className="relative aspect-square md:aspect-auto">
          <img src={BRAND_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        </div>
        <div className="flex flex-col justify-center p-10 md:p-20 bg-[hsl(var(--kb-primary))] text-white">
          <div className="overline text-white/70 mb-4">The Atelier Guild</div>
          <h2 className="font-display text-4xl lg:text-5xl mb-6 leading-tight">
            Every piece carries<br />a <em className="italic">signature</em>.
          </h2>
          <p className="text-white/80 leading-relaxed mb-8">
            Behind every saree is a karigar. Behind every lehenga, months of patience. {branding?.brand_name || "KalkiBazaar"} curates only studios that honour the slow craft — and connects them directly to you.
          </p>
          <Link to="/seller/onboard" className="bg-white text-[hsl(var(--kb-primary))] px-8 py-4 uppercase text-sm tracking-wider font-medium inline-block w-max">
            Meet our ateliers
          </Link>
        </div>
      </section>

      <BookNowBanner />
    </div>
  );
}
